import { css } from '@emotion/react'
import React, { ReactNode } from 'react'
import bgLogoUrl from '../../images/body-bg-logo.svg'
import { mq, rem } from '../../styles/mixin.styles'
import { breakpoints, colors, height, space } from '../../styles/variables.styles'

type Props = {
  title: ReactNode
  subtitle?: string
  subRead?: string
  readText?: ReactNode
  isSimple?: boolean
  description?: string
}

/**
 * PageTitle
 *
 * @param title, readText
 * @returns page title
 */
export const PageTitle: React.FC<Props> = ({ title, subtitle, readText, subRead, isSimple, description }) => {
  return (
    <div css={isSimple ? pageTitleSimpleStyle : pageTitleStyles}>
      {!isSimple && subtitle && <div className="page-sub-title">{subtitle.toUpperCase()}</div>}
      <div className="page-title__inner">
        <h1>{title}</h1>
        {subRead && <p className="page-title__sub-read">{subRead}</p>}
      </div>
      {readText && <p className="page-description">{readText}</p>}
      {description && <p className="page-title__description">{description}</p>}
    </div>
  )
}

const pageTitleStyles = css`
  position: relative;
  margin-left: auto;
  margin-right: auto;
  width: calc(100% - (${space.md} * 2));
  max-width: ${rem(breakpoints.minContent)};
  .page-sub-title {
    font-family: var(--roman-font-family);
    font-weight: 700;
    font-size: clamp(${rem(90)}, 20vw, ${rem(120)});
    line-height: clamp(${rem(103)}, 20vw, ${rem(138)});
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: ${colors.lightColor.hex};
    opacity: 0.5;
    transform-origin: 0 0;
    transform: rotate(90deg) translate(1.5em, -0.7em);
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    width: max-content;
  }
  .page-title__inner {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: calc(${space.md} * 2);
    padding-bottom: ${space.lg};
    h1 {
      letter-spacing: 0.1em;
    }
    .page-title__sub-read {
      max-width: 12em;
      text-align: right;
      font-size: clamp(${rem(12)}, 3vw, ${rem(18)});
      line-height: clamp(${rem(17)}, 4vw, ${rem(26)});
    }
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      right: calc(-1 * ${space.md});
      display: block;
      width: calc(100% + ${space.md});
      height: ${rem(4)};
      background-color: ${colors.mainBlue.hex};
    }
  }
  .page-description {
    font-weight: 700;
    font-size: clamp(${rem(20)}, 2.872vw, ${rem(28)});
    line-height: clamp(${rem(40)}, 5.742vw, ${rem(56)});
    letter-spacing: 0.05em;
    font-feature-settings: 'palt';
    padding-top: ${space.sm};
  }
  .page-title__description {
    line-height: 2;
  }
  ${mq('sm')} {
    width: 84vw;
    margin-right: ${space.lg};
    &::before {
      content: '';
      position: absolute;
      z-index: -1;
      width: 100vw;
      width: 100dvw;
      top: calc(-1 * ${rem(height.header)});
      right: calc(${space.lg} * -1);
      height: 100%;
      min-height: ${rem(247)};
      background-image: url(${bgLogoUrl});
      opacity: 0.3;
      background-size: contain;
      background-position: right top;
      background-repeat: no-repeat;
    }
    .page-title__inner {
      padding-top: calc(${space.md} * 3);
      &::after {
        right: calc((100vw - 100%) / 2 * -1);
        width: calc(100% + ((100vw - 100%) / 2) + ${space.md});
      }
    }
    .page-sub-title {
      letter-spacing: 0.3em;
      transform: rotate(90deg) translate(1.8em, -0.35em);
    }
    .page-description {
      letter-spacing: 0.07em;
    }
  }
  ${mq('lg')} {
    margin-right: 0;
    width: 100%;
    &::before {
      top: calc(-1 * ${rem(height.headerMax)});
      right: calc((100vw - ${breakpoints.content}px) / 2 * -1);
      height: ${rem(427)};
    }
    .page-title__inner {
      align-items: baseline;
      &::after {
        right: calc((100vw - ${rem(breakpoints.minContent)}) / 2 * -1);
        width: calc(${rem(breakpoints.minContent)} + ((100vw - ${rem(breakpoints.minContent)}) / 2) + ${space.lg});
      }
      .page-title__sub-read {
        max-width: none;
      }
    }
    .page-sub-title {
      transform: rotate(90deg) translate(1.6em, 0.4em);
    }
  }
`

const pageTitleSimpleStyle = css`
  width: min(calc(100% - ${space.md} * 2), ${rem(breakpoints.content)});
  margin: 0 auto;
  text-align: center;
  padding-top: clamp(${space.lg}, 5vw, calc(${space.lg} * 2));
  padding-bottom: clamp(${space.lg}, 5vw, calc(${space.lg} * 2));
  .page-title__inner {
    > h1 {
      font-size: clamp(${rem(30)}, 5vw, ${rem(40)});
      line-height: clamp(${rem(43)}, 6vw, ${rem(58)});
    }
  }
  .page-description {
    margin-top: ${rem(18)};
    font-size: clamp(${rem(14)}, 3vw, ${rem(16)});
  }
`
